import { Form, Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

import { QuestionWrapper, InfoBlock } from '../../components';
import { FinanceAnalyseFormValues, ClientFormValues } from '../../models/Forms';
import { I18nNameSpaces } from '../../services/i18n';
import { Row, Col } from 'antd';
import { formatCurrency } from 'common/services/currency';
import { RadioField, SliderWithNumber } from '../../fields';
import { makeFinanceAnalyseSchema } from './schema';

interface Props {
  initialValues?: Partial<FinanceAnalyseFormValues>;
  customerValues?: ClientFormValues;
  bindSubmit?: (formik: FormikProps<FinanceAnalyseFormValues>) => void;
  onSubmit: (values: FinanceAnalyseFormValues) => void;
}

export default function FinancesAnalyseForm({ onSubmit, initialValues, bindSubmit, customerValues }: Props) {
  const { t } = useTranslation(I18nNameSpaces.Forms);
  const schema = makeFinanceAnalyseSchema(t);

  function handleOnSubmit(values: FinanceAnalyseFormValues) {
    onSubmit && onSubmit(values);
  }

  function getReserve(values: FinanceAnalyseFormValues) {
    if (customerValues) {
      if (customerValues.salary === 'employed' || customerValues.salary === 'freelancer') {
        return formatCurrency(values.monthExpenses * 12);
      }

      return formatCurrency(values.liquidity * 0.4);
    }

    return '';
  }

  function renderForm(formik: FormikProps<FinanceAnalyseFormValues>) {
    if (bindSubmit) {
      bindSubmit(formik);
    }
    const { values } = formik;
    return (
      <div className="company-form">
        <Form>
          <QuestionWrapper>
            <QuestionWrapper.Divider type="bordered">
              <SliderWithNumber
                name="liquidity"
                label={t('financeAnalyse.liquidity.label')}
                tooltip={t('financeAnalyse.liquidity.info')}
                defaultValue={0}
                min={0}
              />
            </QuestionWrapper.Divider>
            <QuestionWrapper.Divider type="bordered">
              <SliderWithNumber
                name="capitalInvesting"
                label={t('financeAnalyse.capitalInvesting.label')}
                tooltip={t('financeAnalyse.capitalInvesting.info')}
                defaultValue={0}
                min={0}
              />
            </QuestionWrapper.Divider>
            <QuestionWrapper.Divider type="bordered">
              <SliderWithNumber
                name="monthSalary"
                label={t('financeAnalyse.monthSalary.label')}
                tooltip={t('financeAnalyse.monthSalary.info')}
                defaultValue={0}
                min={0}
              />
            </QuestionWrapper.Divider>
            <QuestionWrapper.Divider type="bordered">
              <SliderWithNumber
                name="monthExpenses"
                label={t('financeAnalyse.monthExpenses.label')}
                tooltip={t('financeAnalyse.monthExpenses.info')}
                defaultValue={0}
                min={0}
              />
            </QuestionWrapper.Divider>
            <QuestionWrapper.Divider>
              <Row gutter={30}>
                <Col xs={24} md={12}>
                  <InfoBlock
                    title={t('financeAnalyse.results.disposableBalance')}
                    value={formatCurrency(Math.max(0, values.monthSalary - values.monthExpenses))}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <InfoBlock title={t('financeAnalyse.results.recommendedReserve')} value={getReserve(values)} />
                </Col>
              </Row>
            </QuestionWrapper.Divider>
          </QuestionWrapper>
          <QuestionWrapper>
            <QuestionWrapper.Divider>
              <QuestionWrapper.Title>{t('financeAnalyse.disposableResources.label')}</QuestionWrapper.Title>
              <RadioField
                name="disposableResources"
                type="vertical"
                options={[
                  {
                    label: t('financeAnalyse.disposableResources.options.a'),
                    value: 'a',
                  },
                  {
                    label: t('financeAnalyse.disposableResources.options.b'),
                    value: 'b',
                  },
                  {
                    label: t('financeAnalyse.disposableResources.options.c'),
                    value: 'c',
                  },
                  {
                    label: t('financeAnalyse.disposableResources.options.d'),
                    value: 'd',
                  },
                  {
                    label: t('financeAnalyse.disposableResources.options.e'),
                    value: 'e',
                  },
                ]}
              />
            </QuestionWrapper.Divider>
          </QuestionWrapper>
        </Form>
      </div>
    );
  }

  return (
    <Formik
      initialValues={{
        liquidity: initialValues?.liquidity ?? 0,
        capitalInvesting: initialValues?.capitalInvesting ?? 0,
        monthExpenses: initialValues?.monthExpenses ?? 0,
        monthSalary: initialValues?.monthSalary ?? 0,
        disposableResources: initialValues?.disposableResources ?? '',
      }}
      onSubmit={handleOnSubmit}
      validationSchema={schema}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {renderForm}
    </Formik>
  );
}
